// Quote Builder — W8 Cabinet Configurator (port of kos-qb-v3.js:11368–
// ~11650 `renderCabinetConfigurator` + `_qbv3w8AddCabinet` + handlers).
//
// CORRECT picker (replaces the WRONG non-W8 `renderCabinetPicker` port
// that lived here previously — same situation as the door-picker swap
// on 2026-05-15). The W8 picker is mounted from picker-area.ts when a
// DTN cab has been selected via the left-pane W8 list; it shows the
// per-cab configurator with cab.desc/code subtitle, PG-aware unit price
// summary, kind-specific drawer/oven/wall blocks, True Handleless + gable
// reduction, board-finish select with PG optgroups + 40%-off-list badge,
// edging, notes, and an Add-to-quote button that builds a QuoteItem.
//
// DTN-only for Phase 1 (Steve 2026-05-15). Non-DTN suppliers continue to
// use the previous code paths (not currently mounted via this picker).
//
// Wires to:
//   • GET /api/dtn/finishes         — full 313-finish board-group list
//   • The session-defaults pubsub   — auto-seeds spec.finish on cab select
//   • The selected-cab pubsub       — re-mounts when a new cab is clicked
//
// Mount API: mountCabinetPicker(host, store, cab) where `cab` is a W8Cab
// (DtnCabRow | W8VirtualCab) carried by the selected-cab pubsub.

import { alertDialog } from "@tkos/shared-ui";
import { repaintPreservingScroll } from "@tkos/shared-ui";
import type {
  DtnFinishRow,
  Hinging,
  QuoteItem,
  SessionFinish,
  W8Cab,
  W8ConfigKind,
  W8Spec,
  W8VirtualCab,
} from "./types";
import type { QuoteStore } from "./state";
import { escapeHtml, fmtGbp, newId } from "./formatters";
import { recalcItem } from "./pricing";
import {
  getSessionFinish,
  loadFinishesFor,
  subscribeSessionFinish,
} from "./session-defaults";
import {
  getSelectedCab,
  setSelectedCab,
  subscribeSelectedCab,
} from "./picker-selection";

// DTN PickerSupplier.key — used to scope session-defaults lookups.
export const DTN_KEY = "Trade Kitchens by DTN";

// PG → discount ladder (verbatim kos-qb-v3.js:11493). Index 0 is unused;
// 1..5 map to the strings shown on the qbv3w8-pgbadge.
export const DISC_LABEL: readonly string[] = [
  "",
  "40% off list",
  "30% off list",
  "20% off list",
  "10% off list",
  "list price",
];

// ── detectConfigKind — verbatim kos-qb-v3.js:11309–11318 ─────────────────

function detectConfigKind(cab: W8Cab): W8ConfigKind {
  if (!cab) return "standard";
  const d = (cab.desc || "").toUpperCase();
  const t = cab.unit_type || "";
  if (/DRAWER PACK|DWR PACK|2DR\b|3DR\b|4DR\b|DRAWERS?\s/.test(d) && !/OVEN|HOUSING/.test(d)) return "drawer_pack";
  if (/OVEN HOUSING|MICROWAVE HOUSING/.test(d) && /\bTL\b|TALL|LARDER/.test(d + " " + t)) return "tall_oven";
  if (t === "gola" && /WALL/.test(d)) return "gola_wall";
  if (t === "gola") return "gola";
  return "standard";
}

// ── Helpers ──────────────────────────────────────────────────────────────

export function isVirtualCab(cab: W8Cab): cab is W8VirtualCab {
  return cab.unit_type === "drawer_packs" && "_dpConfig" in cab;
}

/** True when the cab represents a Drawer Pack — used by picker-area to
 *  route to the dedicated drawer-pack picker rather than the cabinet
 *  configurator. Drawer-pack chips synthesise virtual cabs with
 *  unit_type="drawer_packs"; detectConfigKind also returns "drawer_pack"
 *  for real DTN cab rows whose desc matches /DRAWER PACK|DWR PACK|.../. */
export function isDrawerPackCab(cab: W8Cab): boolean {
  return cab.unit_type === "drawer_packs" || detectConfigKind(cab) === "drawer_pack";
}

export function cabHeight(cab: W8Cab): number {
  if (isVirtualCab(cab)) return cab.height_mm || cab.h || 0;
  return cab.h || 0;
}

export function cabDepth(cab: W8Cab): number {
  if (isVirtualCab(cab)) return cab.depth_mm || cab.d || 0;
  return cab.d || 0;
}

function isTallUnit(cab: W8Cab): boolean {
  if (["sl", "ml", "tl"].includes(cab.unit_type)) return true;
  if (cab.unit_type === "curved_panels" && cabHeight(cab) >= 1500) return true;
  return false;
}

// Width parsing fallback — SOT line 11324–11325 parses width from a
// 3-or-4 digit run in the desc when the catalogue doesn't carry it.
function parseWidthFromDesc(desc: string): number {
  const m = desc.match(/\b(\d{3,4})\b/);
  return m ? Number(m[1]) : 0;
}

function blankSpec(cab: W8Cab, sessionFinish: SessionFinish | null, defaultZoneId: string): W8Spec {
  const w = isVirtualCab(cab) ? cab.width_mm : parseWidthFromDesc(cab.desc);
  return {
    width: w || "",
    height: cabHeight(cab) || "",
    depth: cabDepth(cab) || "",
    hinge_side: "",
    finish: sessionFinish ? sessionFinishToDtnRow(sessionFinish) : null,
    finish_pg: sessionFinish?.pg ?? 5,
    edging: "",
    notes: "",
    qty: 1,
    zoneId: defaultZoneId,
    drawer_config: "",
    drawer_box: "",
    drawers_below: "",
    gable_reduction: "",
    is_drawerline: false,
    handleless: false,
    top_door: "",
    bottom_door: "",
  };
}

// session-defaults.ts stores a normalised SessionFinish; the W8 picker
// state holds a DtnFinishRow because the Add path needs brand/structure
// for note assembly. When seeding from a session default we fabricate a
// minimal DtnFinishRow — the proper full row gets installed when the
// user picks from the in-picker select.
export function sessionFinishToDtnRow(sf: SessionFinish): DtnFinishRow {
  return {
    brand: "",
    name: sf.name,
    code: sf.code,
    structure: "",
    disc: Number(DISC_LABEL[sf.pg]?.match(/(\d+)%/)?.[1] ?? 0),
    pg: sf.pg,
    label: sf.label,
  };
}

// ── Public mount API ─────────────────────────────────────────────────────

export interface CabinetPickerHandle {
  unmount(): void;
}

export function mountCabinetPicker(
  host: HTMLElement,
  store: QuoteStore,
  cab: W8Cab,
): CabinetPickerHandle {
  const initial = store.get();
  const firstZoneId = initial.zones[0]?.id ?? "";
  const seedFinish = getSessionFinish(DTN_KEY);
  const state = {
    cab,
    spec: blankSpec(cab, seedFinish, firstZoneId),
    finishes: [] as DtnFinishRow[],
    finishesLoading: false,
  };

  // Subscribe to session-finish changes — mirrors SOT lines 10885–10888.
  // Only mutate when DTN's session default changes (the strip only ever
  // emits for DTN under current rules; future suppliers won't match this
  // picker's hardcoded DTN_KEY).
  const unsubSession = subscribeSessionFinish((supplierKey, finish) => {
    if (supplierKey !== DTN_KEY) return;
    if (!finish) return;
    state.spec.finish = sessionFinishToDtnRow(finish);
    state.spec.finish_pg = finish.pg;
    paint();
  });

  // Subscribe to selected-cab changes so a fresh card click re-points
  // this picker at the new cab without an outer re-mount. The picker-area
  // also unmounts/re-mounts on cab swap, but covering this here keeps the
  // local state coherent in case of a soft refresh.
  const unsubCab = subscribeSelectedCab((next) => {
    if (!next) return;
    state.cab = next;
    state.spec = blankSpec(next, getSessionFinish(DTN_KEY), state.spec.zoneId || firstZoneId);
    paint();
  });

  void loadDtnFinishes();

  async function loadDtnFinishes(): Promise<void> {
    if (state.finishesLoading || state.finishes.length > 0) return;
    state.finishesLoading = true;
    try {
      const list = await loadFinishesFor(DTN_KEY);
      // session-defaults.loadFinishesFor returns the normalised
      // SessionFinish shape; we also need the raw DTN rows for the
      // in-picker grouped select. Fetch them directly.
      const res = await fetch("/api/dtn/finishes?limit=1000", { credentials: "include" });
      if (res.ok) {
        const data = await res.json() as { results?: DtnFinishRow[] };
        if (Array.isArray(data.results)) state.finishes = data.results;
      }
      // Keep TS happy and document why we kept the SessionFinish call —
      // it warms the supplier-agnostic cache used by session-defaults.
      void list;
    } catch { /* swallow; renderFinishSelector falls back to "loading" */ }
    state.finishesLoading = false;
    // Bug-2 fix (2026-05-15): the session-defaults strip writes a
    // SessionFinish that we seed into spec.finish via sessionFinishToDtnRow.
    // That synthetic row has empty brand + structure because the
    // SessionFinish shape doesn't carry them; the in-picker finish select
    // matches options by `brand|code|structure`, so the synthetic key
    // (`||<code>||`) never matches a real option and the dropdown stays on
    // the placeholder. Once the real finishes endpoint resolves, look up
    // the canonical row by code and replace the synthetic so the select
    // can bind correctly.
    if (state.spec.finish && state.finishes.length > 0) {
      const real = state.finishes.find((f) => f.code === state.spec.finish?.code);
      if (real) {
        state.spec.finish = real;
        state.spec.finish_pg = real.pg;
      }
    }
    paint();
  }

  /** Full repaint, wrapped so the LEFT pane keeps its scroll position and the
   *  field just used keeps focus — see @tkos/shared-ui repaint-preserving-scroll.ts. */
  function paint(): void {
    repaintPreservingScroll(host, () => {
      host.innerHTML = render(state, store);
      wire();
    });
  }

  function wire(): void {
    const $ = <T extends HTMLElement>(sel: string): T | null => host.querySelector<T>(sel);
    const $$ = (sel: string): HTMLElement[] => Array.from(host.querySelectorAll<HTMLElement>(sel));

    $<HTMLSelectElement>("[data-role='zone']")?.addEventListener("change", (e) => {
      state.spec.zoneId = (e.target as HTMLSelectElement).value;
    });
    $<HTMLInputElement>("[data-role='qty']")?.addEventListener("input", (e) => {
      const v = Number((e.target as HTMLInputElement).value) || 1;
      state.spec.qty = v;
      paint();
    });
    $<HTMLInputElement>("[data-role='width']")?.addEventListener("input", (e) => {
      const v = Number((e.target as HTMLInputElement).value);
      state.spec.width = Number.isFinite(v) && v > 0 ? v : "";
    });
    $<HTMLInputElement>("[data-role='height']")?.addEventListener("input", (e) => {
      const v = Number((e.target as HTMLInputElement).value);
      state.spec.height = Number.isFinite(v) && v > 0 ? v : "";
    });
    $<HTMLInputElement>("[data-role='depth']")?.addEventListener("input", (e) => {
      const v = Number((e.target as HTMLInputElement).value);
      state.spec.depth = Number.isFinite(v) && v > 0 ? v : "";
    });

    $<HTMLSelectElement>("[data-role='drawer-config']")?.addEventListener("change", (e) => {
      state.spec.drawer_config = (e.target as HTMLSelectElement).value;
    });
    $<HTMLSelectElement>("[data-role='drawer-box']")?.addEventListener("change", (e) => {
      state.spec.drawer_box = (e.target as HTMLSelectElement).value;
    });

    $$("[data-role='drawers-below']").forEach((btn) => {
      btn.addEventListener("click", () => {
        const v = btn.dataset["drawersBelow"] as "1" | "2";
        state.spec.drawers_below = v;
        paint();
      });
    });

    $$("[data-role='hinge-side']").forEach((btn) => {
      btn.addEventListener("click", () => {
        state.spec.hinge_side = (btn.dataset["hingeSide"] as "L" | "R" | "B" | "N");
        paint();
      });
    });

    $$("[data-role='drawerline']").forEach((btn) => {
      btn.addEventListener("click", () => {
        state.spec.is_drawerline = btn.dataset["drawerline"] === "true";
        paint();
      });
    });

    $$("[data-role='handleless']").forEach((btn) => {
      btn.addEventListener("click", () => {
        state.spec.handleless = btn.dataset["handleless"] === "true";
        paint();
      });
    });

    $$("[data-role='gable-reduction']").forEach((btn) => {
      btn.addEventListener("click", () => {
        state.spec.gable_reduction = (btn.dataset["gableReduction"] as "L" | "R" | "B");
        paint();
      });
    });

    $<HTMLInputElement>("[data-role='top-door']")?.addEventListener("input", (e) => {
      state.spec.top_door = (e.target as HTMLInputElement).value;
    });
    $<HTMLInputElement>("[data-role='bottom-door']")?.addEventListener("input", (e) => {
      state.spec.bottom_door = (e.target as HTMLInputElement).value;
    });

    $<HTMLSelectElement>("[data-role='finish']")?.addEventListener("change", (e) => {
      const key = (e.target as HTMLSelectElement).value;
      if (!key) {
        state.spec.finish = null;
        state.spec.finish_pg = 5;
      } else {
        const f = state.finishes.find((x) => `${x.brand}|${x.code}|${x.structure}` === key);
        if (f) {
          state.spec.finish = f;
          state.spec.finish_pg = f.pg;
        }
      }
      paint();
    });

    $<HTMLInputElement>("[data-role='edging']")?.addEventListener("input", (e) => {
      state.spec.edging = (e.target as HTMLInputElement).value;
    });
    $<HTMLInputElement>("[data-role='notes']")?.addEventListener("input", (e) => {
      state.spec.notes = (e.target as HTMLInputElement).value;
    });

    $<HTMLButtonElement>("[data-role='cancel']")?.addEventListener("click", () => {
      setSelectedCab(null);
    });
    $<HTMLButtonElement>("[data-role='add']")?.addEventListener("click", () => {
      addCabinetToQuote();
    });
  }

  function addCabinetToQuote(): void {
    const cab = state.cab;
    const s = state.spec;
    const kind = detectConfigKind(cab);

    const missing: string[] = [];
    if (!s.width || Number(s.width) <= 0) missing.push("Width");
    if (!s.finish) missing.push("Board finish");
    if (isTallUnit(cab)) {
      if (!s.top_door) missing.push("Top door size");
      if (!s.bottom_door) missing.push("Bottom door size");
      if (s.handleless && !s.gable_reduction) missing.push("Gable reduction");
    }
    if (missing.length) {
      void alertDialog(`Missing: ${missing.join(", ")}`, { title: "Missing fields" });
      return;
    }

    const pg = s.finish_pg || 5;
    const cabPrices: Record<string, number> = {
      pg1: cab.pg1, pg2: cab.pg2, pg3: cab.pg3, pg4: cab.pg4, pg5: cab.pg5,
    };
    const carcassPrice = cabPrices[`pg${pg}`] ?? cab.pg5 ?? 0;
    const f = s.finish!;

    const noteParts: string[] = [];
    if (s.hinge_side && s.hinge_side !== "N") {
      const label = s.hinge_side === "L" ? "Left" : s.hinge_side === "R" ? "Right" : "Both";
      noteParts.push(`Hinge: ${label}`);
    }
    if (s.is_drawerline) noteParts.push("DRAWERLINE");
    if (s.handleless) {
      noteParts.push("TRUE HANDLELESS");
      if (s.gable_reduction) {
        const gr = s.gable_reduction === "L" ? "Left" : s.gable_reduction === "R" ? "Right" : "Both";
        noteParts.push(`Gable reduction: ${gr}`);
      }
    }
    if (s.top_door) noteParts.push(`Top door: ${s.top_door}mm`);
    if (s.bottom_door) noteParts.push(`Bottom door: ${s.bottom_door}mm`);
    const finishLabel = [f.brand, f.name, f.code ? `(${f.code} ${f.structure})` : ""].filter(Boolean).join(" ").trim();
    noteParts.push(`Finish: ${finishLabel} — PG${pg}`);
    if (s.edging) noteParts.push(`Front edging: ${s.edging}`);
    if (kind === "tall_oven") {
      if (s.drawers_below) noteParts.push(`${s.drawers_below} drawer${s.drawers_below === "2" ? "s" : ""} below oven`);
      if (s.drawer_box) noteParts.push(`Box: ${s.drawer_box}`);
    } else if (kind === "drawer_pack") {
      if (s.drawer_config) noteParts.push(`Drawers: ${s.drawer_config}`);
      if (s.drawer_box) noteParts.push(`Box: ${s.drawer_box}`);
    }
    if (s.notes) noteParts.push(s.notes);

    const price = carcassPrice;
    const cabCode = isVirtualCab(cab) ? (cab._carcassCode ?? cab.code) : cab.code;
    const dpSuffix = isVirtualCab(cab) ? " (complete with drawers)" : "";

    const item: QuoteItem = {
      id: newId("i"),
      category: "cabinets",
      supplier: "Trade Kitchens by DTN",
      code: cabCode,
      desc: `${cab.desc} [PG${pg}]${dpSuffix}`,
      qty: s.qty || 1,
      width: Number(s.width) || (isVirtualCab(cab) ? cab.width_mm : 0),
      height: Number(s.height) || cabHeight(cab),
      depth: Number(s.depth) || cabDepth(cab),
      notes: noteParts.join(" | "),
      // Intentional deviation from SOT (kos-qb-v3.js:11622). SOT stores
      // the PG-indexed price as listPrice with discPct=0 — that masks the
      // saving and makes the line misleading (list = trade, "no discount").
      // Modular records the FULL list (PG5) with the explicit PG discount
      // so retailers see what they're actually saving, and so recalcItem
      // (pricing.ts:31) can re-derive tradePrice via calcTrade — verified:
      // calcTrade(78.09, 40) ≈ 46.85 matches cab.pg1. Totals + retail are
      // unchanged. Approved by Steve 2026-05-15.
      listPrice: cab.pg5 || price,
      discPct: f.disc || 0,
      tradePrice: price,
      markupMode: "pct",
      markupPct: store.get().defaults.markupPct ?? 0,
      markupGbp: 0,
      // recalcItem (below) derives this from listPrice + discPct + markupPct
      // so it stays consistent with the user's actual markup choice.
      retailPrice: 0,
      unitTypeId: cab.unit_type,
      unitTypeLabel: cab.unit_label || "Drawer Packs",
      carcassFinish: `${f.brand} ${f.name}`.trim(),
      carcassPriceGroup: pg,
      edging: s.edging || "",
      hinging: (s.hinge_side === "L" ? "L" : s.hinge_side === "R" ? "R" : s.hinge_side === "B" ? "B" : "N") as Hinging,
      construction: s.handleless ? "true-handleless" : "standard",
      // Additive W8 spec fields — only included when set, to satisfy
      // exactOptionalPropertyTypes and avoid persisting empties.
      ...(s.drawer_config   && { drawerConfig:   s.drawer_config }),
      ...(s.drawer_box      && { drawerBox:      s.drawer_box }),
      ...(s.drawers_below   && { drawersBelow:   s.drawers_below }),
      ...(s.gable_reduction && { gableReduction: s.gable_reduction }),
      ...(s.is_drawerline   && { isDrawerline:   s.is_drawerline }),
      ...(s.top_door        && { topDoor:        s.top_door }),
      ...(s.bottom_door     && { bottomDoor:     s.bottom_door }),
    };
    // Derive tradePrice + retailPrice from listPrice/discPct/markupPct so
    // the line ships internally consistent. Matches every other add path
    // (door-picker, simple-pickers, products-panel, kit-q all call this).
    recalcItem(item, null);

    const zoneId = s.zoneId || store.get().zones[0]?.id || "";
    store.set((d) => {
      const z = d.zones.find((zo) => zo.id === zoneId) ?? d.zones[0];
      if (z) z.items.push(item);
    });

    // Reset qty to 1 so the next add doesn't inherit the previous quantity.
    state.spec.qty = 1;
    // Clear the picker after add — matches SOT cancel-on-add UX.
    setSelectedCab(null);
  }

  paint();

  return {
    unmount(): void {
      unsubSession();
      unsubCab();
      host.innerHTML = "";
    },
  };
}

// ── Render ───────────────────────────────────────────────────────────────

interface PickerState {
  cab: W8Cab;
  spec: W8Spec;
  finishes: DtnFinishRow[];
  finishesLoading: boolean;
}

function render(state: PickerState, store: QuoteStore): string {
  const { cab, spec } = state;
  const kind = detectConfigKind(cab);
  const pg = spec.finish_pg || 5;
  const cabPrices: Record<string, number> = {
    pg1: cab.pg1, pg2: cab.pg2, pg3: cab.pg3, pg4: cab.pg4, pg5: cab.pg5,
  };
  const price = cabPrices[`pg${pg}`] ?? cab.pg5 ?? 0;
  const q = store.get();
  const zoneOpts = q.zones.map(
    (z) => `<option value="${escapeHtml(z.id)}"${z.id === spec.zoneId ? " selected" : ""}>${escapeHtml(z.name)}</option>`,
  ).join("") || `<option value="">— No zone —</option>`;

  let html = `<div class="qb-w8-spec">
    <div class="qb-w8-title"><span class="sup">DTN</span>${escapeHtml(cab.desc)}</div>
    <div class="qb-w8-subtitle">${escapeHtml(cab.code)} · ${escapeHtml(cab.unit_label)}${cab.section ? ` · ${escapeHtml(cab.section)}` : ""}</div>
    <div class="qb-w8-summary">
      <div><div class="lbl">Unit price (PG${pg})</div><div class="val">${fmtGbp(price)}</div></div>
      <div class="right"><span class="qb-w8-summary-mult">× ${spec.qty}</span><span class="qb-w8-summary-total">${fmtGbp(price * spec.qty)}</span></div>
    </div>
    <div class="qb-w8-row two">
      <div><label class="qb-w8-lbl">Zone</label><select class="qb-w8-inp" data-role="zone">${zoneOpts}</select></div>
      <div><label class="qb-w8-lbl">Qty</label><input class="qb-w8-inp" type="number" min="1" value="${spec.qty}" data-role="qty"></div>
    </div>
    <div class="qb-w8-row three">
      <div><label class="qb-w8-lbl">Width (mm)<span class="req">*</span></label><input class="qb-w8-inp" type="number" value="${escapeHtml(String(spec.width))}" data-role="width"></div>
      <div><label class="qb-w8-lbl">Height (mm)</label><input class="qb-w8-inp" type="number" value="${escapeHtml(String(spec.height))}" data-role="height"></div>
      <div><label class="qb-w8-lbl">Depth (mm)</label><input class="qb-w8-inp" type="number" value="${escapeHtml(String(spec.depth))}" data-role="depth"></div>
    </div>`;

  // Kind-specific blocks (verbatim SOT 11393–11436).
  if (kind === "drawer_pack") {
    const dcOpts = ["", "2 x Pan drawers", "2 x Pan drawers + internal cutlery & fascia", "1 x Cutlery + 2 Pan drawers", "2 x Cutlery + 1 Pan drawer", "3 x Cutlery + 1 Pan drawer", "4 x Medium drawers (K height)", "5 x Cutlery drawers (M height)"]
      .map((o) => `<option value="${escapeHtml(o)}"${spec.drawer_config === o ? " selected" : ""}>${o || "— select —"}</option>`)
      .join("");
    const dbOpts = ["", "Antaro", "Legrabox", "Elite/Meta", "MFC Pure"]
      .map((o) => `<option value="${escapeHtml(o)}"${spec.drawer_box === o ? " selected" : ""}>${o || "— select —"}</option>`)
      .join("");
    html += `<div class="qb-w8-row two">
      <div><label class="qb-w8-lbl">Drawer configuration<span class="req">*</span></label><select class="qb-w8-inp" data-role="drawer-config">${dcOpts}</select></div>
      <div><label class="qb-w8-lbl">Drawer box brand<span class="req">*</span></label><select class="qb-w8-inp" data-role="drawer-box">${dbOpts}</select></div>
    </div>`;
  } else if (kind === "tall_oven") {
    const dbOpts = ["", "Antaro", "Legrabox", "Elite/Meta", "MFC Pure"]
      .map((o) => `<option value="${escapeHtml(o)}"${spec.drawer_box === o ? " selected" : ""}>${o || "— select —"}</option>`)
      .join("");
    html += `<div class="qb-w8-row two">
      <div><label class="qb-w8-lbl">Drawers below oven<span class="req">*</span></label><div class="qb-w8-toggle">
        <button type="button" class="${spec.drawers_below === "1" ? "active" : ""}" data-role="drawers-below" data-drawers-below="1">1 drawer</button>
        <button type="button" class="${spec.drawers_below === "2" ? "active" : ""}" data-role="drawers-below" data-drawers-below="2">2 drawers</button>
      </div></div>
      <div><label class="qb-w8-lbl">Drawer box brand<span class="req">*</span></label><select class="qb-w8-inp" data-role="drawer-box">${dbOpts}</select></div>
    </div>`;
  }

  // Hinge side — hidden for drawer packs (SOT 11414–11436).
  const isDrawerPack = cab.unit_type === "drawer_packs" || kind === "drawer_pack";
  if (!isDrawerPack) {
    html += `<div class="qb-w8-row two">
      <div><label class="qb-w8-lbl">Hinge side</label><div class="qb-w8-toggle">
        <button type="button" class="${spec.hinge_side === "L" ? "active" : ""}" data-role="hinge-side" data-hinge-side="L">Left</button>
        <button type="button" class="${spec.hinge_side === "R" ? "active" : ""}" data-role="hinge-side" data-hinge-side="R">Right</button>
        <button type="button" class="${spec.hinge_side === "B" ? "active" : ""}" data-role="hinge-side" data-hinge-side="B">Both</button>
        <button type="button" class="${spec.hinge_side === "N" ? "active" : ""}" data-role="hinge-side" data-hinge-side="N">None</button>
      </div></div>`;
    if (cab.unit_type === "base") {
      html += `<div><label class="qb-w8-lbl">Drawerline?</label><div class="qb-w8-toggle">
        <button type="button" class="${!spec.is_drawerline ? "active" : ""}" data-role="drawerline" data-drawerline="false">No</button>
        <button type="button" class="${spec.is_drawerline ? "active" : ""}" data-role="drawerline" data-drawerline="true">Yes</button>
      </div></div>`;
    } else {
      html += `<div></div>`;
    }
    html += `</div>`;
  } else if (isDrawerPack) {
    // SOT line 11435: clears hinge_side for drawer packs so it never
    // leaks into the notes.
    if (spec.hinge_side !== "N") spec.hinge_side = "N";
  }

  // True Handleless (all units) + gable reduction for tall units.
  const isTall = isTallUnit(cab);
  html += `<div class="qb-w8-row two">
    <div><label class="qb-w8-lbl">True Handleless?</label><div class="qb-w8-toggle">
      <button type="button" class="${!spec.handleless ? "active" : ""}" data-role="handleless" data-handleless="false">No</button>
      <button type="button" class="${spec.handleless ? "active" : ""}" data-role="handleless" data-handleless="true">Yes</button>
    </div></div>`;
  if (spec.handleless && isTall) {
    html += `<div><label class="qb-w8-lbl">Gable reduction<span class="req">*</span></label><div class="qb-w8-toggle">
      <button type="button" class="${spec.gable_reduction === "L" ? "active" : ""}" data-role="gable-reduction" data-gable-reduction="L">Left</button>
      <button type="button" class="${spec.gable_reduction === "R" ? "active" : ""}" data-role="gable-reduction" data-gable-reduction="R">Right</button>
      <button type="button" class="${spec.gable_reduction === "B" ? "active" : ""}" data-role="gable-reduction" data-gable-reduction="B">Both</button>
    </div></div>`;
  } else {
    html += `<div></div>`;
  }
  html += `</div>`;

  // Top/Bottom door size for tall units.
  if (isTall) {
    html += `<div class="qb-w8-row two">
      <div><label class="qb-w8-lbl">Top door size (mm)<span class="req">*</span></label><input class="qb-w8-inp" type="number" value="${escapeHtml(spec.top_door)}" data-role="top-door" placeholder="e.g. 715"></div>
      <div><label class="qb-w8-lbl">Bottom door size (mm)<span class="req">*</span></label><input class="qb-w8-inp" type="number" value="${escapeHtml(spec.bottom_door)}" data-role="bottom-door" placeholder="e.g. 1100"></div>
    </div>`;
  }

  // Finish.
  html += `<div class="qb-w8-finish-block">${renderFinishSelector(state)}</div>`;

  // Edging + Notes.
  html += `<div class="qb-w8-row two">
    <div><label class="qb-w8-lbl">Front edging colour</label><input class="qb-w8-inp" type="text" value="${escapeHtml(spec.edging)}" data-role="edging"></div>
    <div><label class="qb-w8-lbl">Notes</label><input class="qb-w8-inp" type="text" value="${escapeHtml(spec.notes)}" data-role="notes"></div>
  </div>`;

  // Foot — pgbadge + actions.
  const badge = spec.finish
    ? `<span class="qb-w8-pgbadge">PG${pg} — ${spec.finish.disc}% off list</span>`
    : `<em class="qb-w8-hint-em">Select a finish to set price group</em>`;
  html += `<div class="qb-w8-foot">
    <div class="qb-w8-hint">${badge}</div>
    <div class="qb-w8-foot-actions">
      <button type="button" class="qb-btn qb-btn--ghost" data-role="cancel">Cancel</button>
      <button type="button" class="qb-btn qb-btn--primary" data-role="add">Add to quote</button>
    </div>
  </div></div>`;

  return html;
}

function renderFinishSelector(state: PickerState): string {
  const { spec, finishes, finishesLoading } = state;
  if (!finishes.length) {
    const msg = finishesLoading ? "Loading finishes…" : "No finishes loaded";
    return `<label class="qb-w8-lbl">Board finish<span class="req">*</span></label><div class="qb-w8-finish-loading">${msg}</div>`;
  }
  const f = spec.finish;
  const headerBadge = f && f.brand
    ? ` <span class="qb-w8-pgbadge">${escapeHtml(f.brand)} · PG${f.pg}</span>`
    : "";
  let html = `<label class="qb-w8-lbl">Board finish<span class="req">*</span>${headerBadge}</label>
    <select class="qb-w8-inp" data-role="finish">
      <option value="">— Select board finish (sets price group) —</option>`;
  for (const pg of [1, 2, 3, 4, 5] as const) {
    const arr = finishes.filter((x) => x.pg === pg);
    if (!arr.length) continue;
    html += `<optgroup label="PG${pg} — ${DISC_LABEL[pg]}">`;
    for (const fn of arr) {
      const key = `${fn.brand}|${fn.code}|${fn.structure}`;
      const selected = f && `${f.brand}|${f.code}|${f.structure}` === key ? " selected" : "";
      html += `<option value="${escapeHtml(key)}"${selected}>${escapeHtml(fn.brand)} · ${escapeHtml(fn.name)} (${escapeHtml(fn.code)} ${escapeHtml(fn.structure)})</option>`;
    }
    html += `</optgroup>`;
  }
  html += `</select>`;
  return html;
}

// ── Empty-state helper used by picker-area.ts when no cab is selected ──
export function renderCabinetPickerEmptyState(): string {
  return `<div class="qb-w8-empty-state">
    <strong>Nothing selected yet</strong>
    Browse the <strong>DTN catalogue</strong> on the left,<br>click any cabinet to configure it here.
  </div>`;
}

// Re-export the current selected-cab read for callers that need it.
export { getSelectedCab };
